/**
 * Renders a v2 hang plan in the shape the viewer already executes.
 *
 * `ViewportState.applyHangingProtocol` and `Viewport.applyViewportLayout` were
 * written against the v1 DTO: an `appliedHp` with `layout.viewportList`, plus an
 * `hpViewportMap` entry naming the stack and image each viewport should show.
 * That execution path is well-tested in the field — it carries fixes for the
 * priors race, the loader that span forever on stale layout keys, and the
 * first-slot fallback.
 *
 * So rather than rewrite it, this maps the plan onto that shape. The v2 work
 * replaces *matching*, which is where the format actually differs; execution
 * stays as it is.
 *
 * The v1 viewport shape this produces:
 *   { row, column, windowWidth, windowLevel, flipVertically, flipHorizontally,
 *     alignment, alignmentLocked, rotation, zoomFactor }
 * with the flips as "enable"/"disable" strings, not booleans. `zoomFactor`
 * is the one field a v1 hang never carries — see `zoomFactorOf`.
 */

import {
  ACTION_SYNC_MODES,
  HP_VIEWPORT_ALIGNMENT,
  HP_VIEWPORT_FLIP,
  SYNC_MODES,
} from "../../constants/Constants";
import { LINK_SCROLL_SERIES, SCALE, SYNC_ACTIONS } from "./protocolEnums";

const flag = (value) => (value ? HP_VIEWPORT_FLIP.ENABLE : HP_VIEWPORT_FLIP.DISABLE);

/**
 * `linkScrollSeries` -> the viewer's own sync state, as `{ mode, enabled }`.
 *
 * Not a rename: the format's MANUAL means "nothing is linked until the
 * radiologist links it", so it maps to sync *on* — the per-viewport chain icon
 * only renders while sync is enabled and the mode is MANUAL — with no viewports
 * linked yet. `SYNC_MODES.DISABLED` is paired with enabled:false because the
 * viewer tracks the toggle and the mode separately, and a disabled mode left
 * "enabled" would have the Viewport effects re-register sync on the next render.
 */
const LINK_SCROLL_MAP = {
  [LINK_SCROLL_SERIES.OFF]: { mode: SYNC_MODES.DISABLED, enabled: false },
  [LINK_SCROLL_SERIES.MANUAL]: { mode: SYNC_MODES.MANUAL, enabled: true },
  [LINK_SCROLL_SERIES.AUTO]: { mode: SYNC_MODES.AUTOMATIC, enabled: true },
  [LINK_SCROLL_SERIES.POSITION]: { mode: SYNC_MODES.DISTANCE, enabled: true },
};

/**
 * `syncActions` -> the viewer's action-propagation state, as `{ mode, enabled }`.
 *
 * The format's MANUAL is the widest scope, not the narrowest: its label is "All
 * Sync in all viewports", so it maps to ALL. The narrowing modes are SERIES
 * (viewports showing the same series) and LINKED (viewports the scroll
 * synchronizer currently ties together).
 */
const SYNC_ACTIONS_MAP = {
  [SYNC_ACTIONS.OFF]: { mode: ACTION_SYNC_MODES.DISABLED, enabled: false },
  [SYNC_ACTIONS.MANUAL]: { mode: ACTION_SYNC_MODES.ALL, enabled: true },
  [SYNC_ACTIONS.SERIES]: { mode: ACTION_SYNC_MODES.SERIES, enabled: true },
  [SYNC_ACTIONS.LINKED]: { mode: ACTION_SYNC_MODES.LINKED, enabled: true },
};

/**
 * v2 has five alignments; the viewer positions by three of them. The "AndLock"
 * variants are those same three positions plus a standing pin against panning,
 * carried separately as `alignmentLocked` — see AlignmentLockService.
 */
const ALIGNMENT_MAP = {
  center: HP_VIEWPORT_ALIGNMENT.CENTER,
  left: HP_VIEWPORT_ALIGNMENT.LEFT,
  leftAndLock: HP_VIEWPORT_ALIGNMENT.LEFT,
  right: HP_VIEWPORT_ALIGNMENT.RIGHT,
  rightAndLock: HP_VIEWPORT_ALIGNMENT.RIGHT,
};

const LOCKING_ALIGNMENTS = new Set(["leftAndLock", "rightAndLock"]);

/**
 * The zoom this viewport asks for, in canvas pixels per image pixel.
 *
 * That is the Advance Viewer's own unit, not a multiple of the fit: its
 * `setOriginalResolution` is literally `setScale(1)`, and its alignment maths
 * reads `columns * scale`, so `customScaleValue: 2` means every image pixel is
 * drawn two pixels wide whatever size the viewport happens to be.
 *
 *   original      -> 1, one image pixel per canvas pixel
 *   zoom          -> the author's customScaleValue
 *   fit_to_screen -> null, which is where loading already leaves the image
 *   zoom_to_image -> null: `applyHpViewportActions` runs applyFitToScreen for
 *                    every modality but MG, whose zoom-to-tissue crop we have
 *                    no equivalent of
 *
 * Null for anything that is not a usable factor too, so a hand-edited document
 * cannot blank a viewport with a zero or negative scale.
 */
function zoomFactorOf(transforms) {
  if (transforms.scale === SCALE.ORIGINAL) return 1;
  if (transforms.scale !== SCALE.ZOOM) return null;
  const factor = Number(transforms.customScaleValue);
  return Number.isFinite(factor) && factor > 0 ? factor : null;
}

/**
 * @param plan the result of `planHang`
 * @returns `{ appliedHp, viewportDataMap, deferred }`, or null for no plan.
 *          `viewportDataMap` is keyed "row-column", as hpViewportMap expects.
 */
export function planToLegacyHang(plan) {
  if (!plan) return null;

  const deferred = new Set(plan.deferred ?? []);
  const viewportList = [];
  const viewportDataMap = new Map();

  plan.assignments.forEach((assignment) => {
    const { transforms } = assignment;

    viewportList.push({
      row: assignment.row,
      column: assignment.column,
      windowWidth: transforms.windowWidth,
      // v1 calls it windowLevel; the format calls it windowCenter.
      windowLevel: transforms.windowCenter,
      flipVertically: flag(transforms.flipVertically),
      flipHorizontally: flag(transforms.flipHorizontally),
      alignment: ALIGNMENT_MAP[transforms.alignment] ?? HP_VIEWPORT_ALIGNMENT.CENTER,
      // "& Lock": hold that alignment against panning, and against whatever
      // series the radiologist loads into the viewport afterwards, until they
      // take the lock off from the viewport padlock.
      alignmentLocked: LOCKING_ALIGNMENTS.has(transforms.alignment),
      rotation: transforms.rotation ?? 0,
      // Absolute canvas-px-per-image-px, or null to leave the fit alone.
      zoomFactor: zoomFactorOf(transforms),
    });

    viewportDataMap.set(`${assignment.row}-${assignment.column}`, {
      row: assignment.row,
      column: assignment.column,
      // Which of the open studies the stack index belongs to, aligned to the
      // viewer's priorStudyData. 0 is the study being reported on, which is
      // every viewport of a protocol that never addresses a prior.
      matchingStudyIndex: assignment.studyIndex ?? 0,
      matchingStackIndex: assignment.seriesIndex,
      matchingImageIndex: assignment.instanceIndex,
    });
  });

  return {
    appliedHp: {
      // Carried so the viewer can tell v2 hangs apart and reload the right one.
      v2: true,
      groupId: plan.groupId,
      categoryId: plan.categoryId,
      name: plan.name,
      description: plan.category?.description ?? "",
      // What the protocol asks the synchronizer to do, or null when it says
      // nothing — the viewer then keeps its own mode. v1 hangs never carry this.
      sync: LINK_SCROLL_MAP[plan.linkScrollSeries] ?? null,
      // The same, for propagating actions rather than scroll position.
      actionSync: SYNC_ACTIONS_MAP[plan.syncActions] ?? null,
      // Rides on appliedHp so switching protocol from the toolbar menu — which
      // sets appliedHp and nothing else — updates the prior thumbnails too.
      expandAllPriors: Boolean(plan.expandAllPriors),
      layout: {
        name: plan.name,
        rows: plan.layout.rows,
        columns: plan.layout.columns,
        // Only assigned viewports: an unmatched one has nothing to hang, and
        // an entry with no stack would leave the loader waiting on it.
        viewportList,
      },
    },
    viewportDataMap,
    deferred: [...deferred],
  };
}
